import { css, CSSResult, unsafeCSS } from 'lit';
import { rem, color, spacing, text } from '@thiememeulenhoff/lit-sdk';
import { BreakpointSmall } from '@thiememeulenhoff/tokens/core';
import { ToasterStyle, ToasterType, toasterTypes } from './toaster.types';
import { tagName } from '../../../helpers/element.helper';

export const tag = tagName('toaster-message');

const styles: Array<CSSResult> = [];

styles.push(css`
  :host {
    --calculated-height: ${rem(100)};

    ${text('body', 'medium')};

    border-inline-end-style: solid;
    border-inline-end-width: ${rem(2)};
    border-radius: ${spacing(100)} 0 0 ${spacing(100)};
    color: ${color('grey', 900)};
    display: flex;
    gap: ${spacing(75)};
    inline-size: ${rem(330)};
    overflow: hidden;
    padding: ${spacing(150)};

    h3,
    p {
      margin: 0;
    }

    h3 {
      ${text('body', 'medium', 'bold')};
      flex: 1;
      gap: ${spacing(75)};
    }

    a-icon-button::part(button) {
      min-block-size: ${rem(26)};
    }

    .header {
      display: flex;
    }

    .icon-wrapper {
      padding-block-start: ${rem(4)};
    }

    .content-wrapper {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: ${spacing(100)};
    }
  }

  :host(:not([dismissable])) .close-button {
    display: none;
  }

  :host(.move-in) {
    animation: moveLeft 0.25s forwards;
  }

  :host(.dismissing) {
    animation: moveRight 0.25s forwards;
  }

  :host(.shrink) {
    animation: shrink 0.25s forwards;
  }

  @media (width <= ${unsafeCSS(BreakpointSmall)}) {
    :host {
      inline-size: calc(100dvw - ${spacing(200)});
      margin-inline: ${spacing(100)};
    }
  }

  @keyframes moveLeft {
    0% {
      transform: translateX(100%);
    }

    100% {
      transform: translateX(0);
    }
  }

  @keyframes moveRight {
    0% {
      opacity: 1;
      transform: translateX(0);
    }
    100% {
      opacity: 0;
      transform: translateX(120%);
    }
  }

  @keyframes shrink {
    0% {
      block-size: var(--calculated-height);
      padding: ${spacing(150)};
      transform: translateX(120%);
    }
    100% {
      block-size: 0;
      margin-block-start: 0;
      padding: 0;
      transform: translateX(120%);
    }
  }
`);

export const ToasterStyles: Record<ToasterType, ToasterStyle> = {
  success: {
    icon: { color: color('green', 500), name: 'success-solid' },
    background: color('green', 100),
    border: color('green', 500),
  },
  error: {
    icon: { color: color('red', 600), name: 'error-solid' },
    background: color('red', 100),
    border: color('red', 600),
  },
  warning: {
    icon: { color: color('orange', 500), name: 'danger-solid' },
    background: color('orange', 100),
    border: color('orange', 500),
  },
  info: {
    icon: { color: color('blue', 500), name: 'info-solid' },
    background: color('blue', 100),
    border: color('blue', 500),
  },
  neutral: {
    icon: { color: color('grey', 600), name: 'user' },
    background: color('grey', 100),
    border: color('grey', 600),
  },
};

const toasterMessageTypeStyle = (toasterType: ToasterType) => {
  const selector = unsafeCSS(`[type=${toasterType}]`);
  const iconClass = unsafeCSS(`.toaster-icon-${toasterType}`);
  const { background, border, icon } = ToasterStyles[toasterType];

  return css`
    :host(${selector}) {
      background-color: ${background};
      border-color: ${border};
    }

    ${iconClass} {
      color: ${icon.color};
    }
  `;
};

toasterTypes.forEach((type: ToasterType) =>
  styles.push(toasterMessageTypeStyle(type)),
);

export const toasterMessageStyles = styles;
